import React, { useState, useEffect, useRef } from "react";
import { MdClose } from "react-icons/md";
import { useZoomNotification } from "../../hooks/home/tabs/dashboard/components/useZoomNotification";
import { safeStorage } from "../../utils/storage";
import "../../pages/home/tabs/dashboard/components/Announcements.css";
import "./NoticeIsland.css";

const POPUP_DISMISS_KEY = "announcement_popup_dismissed";
const DRAG_POSITION_KEY = "notice_island_position";
const DEFAULT_ROTATE_MS = 5500;
const DEFAULT_NEW_DAYS = 7;
const DRAG_THRESHOLD_PX = 5;
const MOBILE_BREAKPOINT_PX = 768;

const isDesktopViewport = () =>
  typeof window !== "undefined" && window.innerWidth > MOBILE_BREAKPOINT_PX;

const clampPosition = (left, top, width, height) => {
  const maxLeft = Math.max(window.innerWidth - width, 0);
  const maxTop = Math.max(window.innerHeight - height, 0);
  return {
    left: Math.min(Math.max(left, 0), maxLeft),
    top: Math.min(Math.max(top, 0), maxTop),
  };
};

const getNotificationKey = (item, dateObj) => {
  if (item?.id) return item.id;
  const dateKey = dateObj && !isNaN(dateObj.getTime()) ? dateObj.getTime() : "";
  return `${item?.title || ""}__${dateKey}`;
};

const getTodayString = () => new Date().toDateString();

const parseDate = (date) => {
  if (!date) return null;
  if (date && typeof date === "object" && date.seconds !== undefined) {
    return new Date(date.seconds * 1000);
  }
  if (date && typeof date.toDate === "function") return date.toDate();
  if (date instanceof Date) return date;
  return new Date(date);
};

const formatDateLong = (date) => {
  const dateObj = parseDate(date);
  if (!dateObj || isNaN(dateObj.getTime())) return "";
  return `${dateObj.getFullYear()}. ${dateObj.getMonth() + 1}. ${dateObj.getDate()}.`;
};

const normalizeDescription = (text) => {
  if (typeof text !== "string") return text;
  return text.replace(/\\r\\n|\\n/g, "\n");
};

const isNoticeNew = (createdAt, withinDays = DEFAULT_NEW_DAYS) => {
  const date = parseDate(createdAt);
  if (!date || isNaN(date.getTime())) return false;
  const diffMs = Date.now() - date.getTime();
  return diffMs >= 0 && diffMs <= withinDays * 24 * 60 * 60 * 1000;
};

/**
 * 전 탭 공통 공지 Dynamic Island.
 * 클릭 시 기존 공지 상세 팝업을 연다. 홈 진입 시 최신 공지 자동 팝업도 담당.
 */
const NoticeIsland = ({
  rotateMs = DEFAULT_ROTATE_MS,
  noticeNewDays = DEFAULT_NEW_DAYS,
}) => {
  const { notifications, isReady } = useZoomNotification();
  const [noticeIndex, setNoticeIndex] = useState(0);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupNotice, setPopupNotice] = useState(null);
  const [dontShowToday, setDontShowToday] = useState(false);
  const [autoPopupMode, setAutoPopupMode] = useState(false);
  const hasCheckedPopup = useRef(false);
  const wrapRef = useRef(null);
  const dragStateRef = useRef({
    dragging: false,
    moved: false,
    startX: 0,
    startY: 0,
    originLeft: 0,
    originTop: 0,
  });
  const [dragPos, setDragPos] = useState(null);
  const [isDesktop, setIsDesktop] = useState(isDesktopViewport());

  const visibleNotifications = (notifications || []).filter((n) => n.is_show);
  const currentNotice =
    visibleNotifications[
      noticeIndex % Math.max(visibleNotifications.length, 1)
    ] || null;
  const showNewBadge = currentNotice
    ? isNoticeNew(currentNotice.created_at, noticeNewDays)
    : false;

  useEffect(() => {
    if (visibleNotifications.length <= 1) return undefined;
    const timer = setInterval(() => {
      setNoticeIndex((prev) => (prev + 1) % visibleNotifications.length);
    }, Math.max(1500, rotateMs));
    return () => clearInterval(timer);
  }, [rotateMs, visibleNotifications.length]);

  // 홈 진입 시 최신 공지 자동 팝업 (오늘 하루 보지 않기 제외)
  useEffect(() => {
    if (hasCheckedPopup.current) return;
    if (!isReady) return;
    hasCheckedPopup.current = true;

    const latest = visibleNotifications[0];
    if (!latest) return;

    const latestDate = parseDate(latest.created_at);
    const latestKey = getNotificationKey(latest, latestDate);

    let dismissed = null;
    try {
      const raw = safeStorage.getItem(POPUP_DISMISS_KEY);
      dismissed = raw ? JSON.parse(raw) : null;
    } catch (e) {
      dismissed = null;
    }

    const isDismissedToday =
      dismissed &&
      dismissed.key === latestKey &&
      dismissed.date === getTodayString();

    if (!isDismissedToday) {
      setPopupNotice(latest);
      setAutoPopupMode(true);
      setDontShowToday(false);
      setPopupOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, visibleNotifications.length]);

  // 데스크톱에서 저장된 드래그 위치 복원
  useEffect(() => {
    if (!isDesktopViewport()) return;
    try {
      const raw = safeStorage.getItem(DRAG_POSITION_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (typeof parsed?.left === "number" && typeof parsed?.top === "number") {
        setDragPos(parsed);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    const handleResize = () => setIsDesktop(isDesktopViewport());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePointerMove = (e) => {
    const state = dragStateRef.current;
    if (!state.dragging) return;
    const dx = e.clientX - state.startX;
    const dy = e.clientY - state.startY;
    if (!state.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    state.moved = true;
    const wrapEl = wrapRef.current;
    const width = wrapEl?.offsetWidth || 0;
    const height = wrapEl?.offsetHeight || 0;
    setDragPos(
      clampPosition(state.originLeft + dx, state.originTop + dy, width, height)
    );
  };

  const handlePointerUp = () => {
    const state = dragStateRef.current;
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", handlePointerUp);
    state.dragging = false;
    if (state.moved) {
      setDragPos((prev) => {
        if (prev) {
          try {
            safeStorage.setItem(DRAG_POSITION_KEY, JSON.stringify(prev));
          } catch (e) {
            // ignore
          }
        }
        return prev;
      });
    }
  };

  const handlePointerDown = (e) => {
    if (!isDesktopViewport()) return;
    if (typeof e.button === "number" && e.button !== 0) return;
    const wrapEl = wrapRef.current;
    if (!wrapEl) return;
    const rect = wrapEl.getBoundingClientRect();
    dragStateRef.current = {
      dragging: true,
      moved: false,
      startX: e.clientX,
      startY: e.clientY,
      originLeft: rect.left,
      originTop: rect.top,
    };
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
  };

  useEffect(() => {
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleIslandClick = () => {
    if (dragStateRef.current.moved) {
      dragStateRef.current.moved = false;
      return;
    }
    openNoticePopup(currentNotice);
  };

  const openNoticePopup = (notice) => {
    if (!notice) return;
    setPopupNotice(notice);
    setAutoPopupMode(false);
    setDontShowToday(false);
    setPopupOpen(true);
  };

  const handleClosePopup = () => {
    if (autoPopupMode && dontShowToday && popupNotice) {
      const dateObj = parseDate(popupNotice.created_at);
      const key = getNotificationKey(popupNotice, dateObj);
      safeStorage.setItem(
        POPUP_DISMISS_KEY,
        JSON.stringify({ key, date: getTodayString() })
      );
    }
    setPopupOpen(false);
    setPopupNotice(null);
    setAutoPopupMode(false);
  };

  if (!isReady || visibleNotifications.length === 0) {
    return null;
  }

  return (
    <>
      <div
        className="notice-island-wrap"
        ref={wrapRef}
        onPointerDown={handlePointerDown}
        style={
          isDesktop && dragPos
            ? {
                left: dragPos.left,
                top: dragPos.top,
                right: "auto",
                bottom: "auto",
                transform: "none",
                justifyContent: "flex-start",
              }
            : undefined
        }
      >
        <button
          type="button"
          className="notice-island"
          onClick={handleIslandClick}
          aria-label={`공지: ${currentNotice?.title || ""}`}
        >
          <span className="notice-island-dot" aria-hidden="true" />
          <span className="notice-island-label">공지</span>
          <span className="notice-island-divider" aria-hidden="true" />
          <span className="notice-island-title">{currentNotice?.title}</span>
          {showNewBadge && <span className="notice-island-new">NEW</span>}
        </button>
      </div>

      {popupOpen && popupNotice && (
        <div
          className="announcement-popup-overlay"
          onClick={handleClosePopup}
          role="dialog"
          aria-modal="true"
          aria-labelledby="notice-island-popup-title"
        >
          <div
            className="announcement-popup-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="announcement-popup-header">
              <span className="announcement-popup-date">
                {formatDateLong(popupNotice.created_at)}
              </span>
              <button
                type="button"
                className="announcement-popup-close"
                onClick={handleClosePopup}
                aria-label="닫기"
              >
                <MdClose size={22} />
              </button>
            </div>
            <div className="announcement-popup-body">
              <h2
                id="notice-island-popup-title"
                className="announcement-popup-title"
              >
                {popupNotice.title}
              </h2>
              {popupNotice.image_url && (
                <div className="announcement-popup-image-wrap">
                  <img
                    src={popupNotice.image_url}
                    alt=""
                    className="announcement-popup-image"
                  />
                </div>
              )}
              {popupNotice.discription && (
                <div className="announcement-popup-description">
                  {normalizeDescription(popupNotice.discription)}
                </div>
              )}
            </div>
            <div className="announcement-popup-footer">
              {autoPopupMode ? (
                <label className="announcement-popup-checkbox">
                  <input
                    type="checkbox"
                    checked={dontShowToday}
                    onChange={(e) => setDontShowToday(e.target.checked)}
                  />
                  오늘 하루 보지 않기
                </label>
              ) : (
                <span />
              )}
              <button
                type="button"
                className="announcement-popup-close-btn"
                onClick={handleClosePopup}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NoticeIsland;
